import { App, Button, Input, Select, Space, Tooltip } from 'antd';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FileSecretFieldType as FieldType,
  FileSecretField as FileSecretFieldType,
} from '../../../interfaces/FileSecret';
import { getSecretFieldTypeMeta, SECRET_FIELD_TYPES } from '../../../utils/fileSecretFieldTypes';
import { getSecretFieldSoftWarning } from '../../../utils/fileSecretFieldValidation';
import { Icon } from '../../Core/Icon';
import { PasswordFeatures } from '../../Common/PasswordFeatures';

interface Props {
  field: FileSecretFieldType;
  mode: 'view' | 'edit';
  onChange?: (field: FileSecretFieldType) => void;
  onRemove?: () => void;
  /** Autofocus value input (create-custom flow). */
  autoFocus?: boolean;
  /** Optional DnD handle — sits on value row (left of input). */
  dragHandle?: ReactNode;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0');
  return { value, label: value };
});

/** Year select: currentYear − 100 … currentYear + 100 (inclusive). */
export function buildYearOptions(currentYear = new Date().getFullYear()) {
  return Array.from({ length: 201 }, (_, i) => {
    const year = String(currentYear - 100 + i);
    return { value: year, label: year };
  });
}

const YEAR_OPTIONS = buildYearOptions();

function optionLabel(
  field: FileSecretFieldType,
  option: string,
  t: (key: string) => string,
): string {
  if (field.key === 'brand') {
    return t(`secret.card.brands.${option}`);
  }
  if (field.key === 'title') {
    return t(`secret.identity.titles.${option}`);
  }
  return option;
}

export const FileSecretField: React.FC<Props> = ({
  field,
  mode,
  onChange,
  onRemove,
  autoFocus = false,
  dragHandle,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isLocked = Boolean(field.locked);
  const ui = field.ui;
  const isPassword = field.type === 'password' || Boolean(ui?.mask);
  const isEmail = field.type === 'email';
  const isPhone = field.type === 'phone';
  const isNumber = field.type === 'number';
  // Multiline: textarea type or ui.multiline — plain text (e.g. cardholder) stays single-line
  const isMultiline = field.type === 'textarea' || Boolean(ui?.multiline);
  // Brand/title only: key-based Select (no generic select type in custom picker)
  const isKeyBasedSelect = field.key === 'brand' || field.key === 'title';
  const isMonth = field.type === 'month';
  const isYear = field.type === 'year';
  const showGenerator = field.type === 'password' || Boolean(ui?.generator);
  const showUpload = Boolean(ui?.upload);
  const isReadOnly = mode === 'view';
  const typeMeta = getSecretFieldTypeMeta(field.type);
  const knownTypeValues = SECRET_FIELD_TYPES.map((ft) => ft.value);
  // Soft hint only — never blocks Save (create/edit modal/viewer).
  const softWarning = getSecretFieldSoftWarning(field.type, field.value);

  const handleTypeChange = (type: FieldType) => {
    // Preset keyed fields (website, username, …) keep a fixed type.
    if (isLocked || field.key) return;
    const meta = getSecretFieldTypeMeta(type);
    onChange?.({ ...field, type, label: t(meta.labelKey) });
  };

  const handleLabelChange = (label: string) => {
    onChange?.({ ...field, label });
  };

  const handleValueChange = (value: string) => {
    onChange?.({ ...field, value });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(field.value);
      message.success(t('secret.password.copied'));
    } catch {
      message.error('Failed to copy');
    }
  };

  const handleGeneratePassword = (password: string) => {
    onChange?.({ ...field, value: password });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      onChange?.({ ...field, value: text });
    } catch {
      message.error(t('secret.ssh.uploadError'));
    }
  };

  const renderSelectOptions = () => {
    if (isMonth) return MONTH_OPTIONS;
    if (isYear) return YEAR_OPTIONS;
    return (ui?.options ?? []).map((option) => ({
      value: option,
      label: optionLabel(field, option, t),
    }));
  };

  /** Plain string for read-only Input (masked password / select labels). */
  const viewDisplayValue = (() => {
    if (isPassword && !passwordVisible) return '••••••••••••';
    if (isKeyBasedSelect || isMonth || isYear) {
      const raw = field.value;
      if (!raw) return '';
      if (isKeyBasedSelect) return optionLabel(field, raw, t);
      return raw;
    }
    return field.value;
  })();

  const copyButton = (
    <Tooltip title={t('secret.password.copy')}>
      <Button
        icon={<Icon name="CopyIcon" className="h-4 w-4" />}
        onClick={handleCopy}
        aria-label={t('secret.password.copy')}
        data-testid={`secret-field-copy-${field.id}`}
      />
    </Tooltip>
  );

  const visibilityButton = isPassword ? (
    <Tooltip title={passwordVisible ? t('secret.password.hide') : t('secret.password.show')}>
      <Button
        icon={<Icon name={passwordVisible ? 'EyeOffIcon' : 'EyeIcon'} className="h-4 w-4" />}
        onClick={() => setPasswordVisible(!passwordVisible)}
        aria-label={passwordVisible ? t('secret.password.hide') : t('secret.password.show')}
      />
    </Tooltip>
  ) : null;

  const typeOptionLabel = (
    labelKey: string,
    iconName: (typeof SECRET_FIELD_TYPES)[number]['iconName'],
  ) => (
    <span className="flex items-center gap-1.5">
      <Icon name={iconName} className="h-3.5 w-3.5 shrink-0" />
      <span>{t(labelKey)}</span>
    </span>
  );

  // Type picker: unlocked custom fields only (no preset key). Website/url etc. stay fixed.
  const showTypeSelect = !isReadOnly && !isLocked && !field.key;

  const textInputType = isEmail ? 'email' : isPhone ? 'tel' : isNumber ? 'number' : 'text';

  return (
    <div
      className="flex flex-col gap-1.5 py-1"
      data-testid={`secret-field-${field.id}`}
      data-locked={isLocked ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2">
        {showTypeSelect && (
          <Select
            value={
              (knownTypeValues.includes(field.type as FieldType) ? field.type : 'text') as FieldType
            }
            onChange={handleTypeChange}
            className="w-36"
            size="small"
            options={SECRET_FIELD_TYPES.map((ft) => ({
              value: ft.value,
              label: typeOptionLabel(ft.labelKey, ft.iconName),
            }))}
          />
        )}
        {isReadOnly ? (
          <span className="flex flex-1 items-center gap-2 font-medium">
            <Tooltip title={t(typeMeta.labelKey)}>
              <span className="text-gray-dark inline-flex items-center">
                <Icon name={typeMeta.iconName} className="h-4 w-4" />
              </span>
            </Tooltip>
            {field.label}
          </span>
        ) : showTypeSelect ? (
          <Input
            value={field.label}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder={t('secret.fieldLabel')}
            className="flex-1 font-medium"
            size="small"
            variant="borderless"
            data-testid={`secret-field-label-${field.id}`}
          />
        ) : (
          <div className="flex flex-1 items-center gap-2">
            <Tooltip title={t(typeMeta.labelKey)}>
              <span className="text-gray-dark inline-flex items-center">
                <Icon name={typeMeta.iconName} className="h-4 w-4" />
              </span>
            </Tooltip>
            <span className="font-medium">{field.label || t('secret.fieldLabel')}</span>
          </div>
        )}
        {!isReadOnly && onRemove && !isLocked && (
          <Tooltip title={t('secret.removeField')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<Icon name="TrashIcon" className="h-4 w-4" />}
              onClick={onRemove}
              data-testid={`secret-field-remove-${field.id}`}
            />
          </Tooltip>
        )}
      </div>

      <div className="flex items-start gap-1">
        {dragHandle}
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {isReadOnly ? (
            isMultiline ? (
              <div className="flex min-w-0 flex-1 items-start gap-1">
                <Input.TextArea
                  readOnly
                  value={viewDisplayValue}
                  className="flex-1 font-mono"
                  autoSize={{ minRows: 2, maxRows: 8 }}
                  data-testid={`secret-field-view-${field.id}`}
                />
                <div className="flex shrink-0 flex-col gap-1">
                  {visibilityButton}
                  {copyButton}
                </div>
              </div>
            ) : (
              // antd Space.Compact: Input + action Button(s) (copy / show) as compact group
              <Space.Compact block className="min-w-0 flex-1">
                <Input
                  readOnly
                  value={viewDisplayValue}
                  className="font-mono"
                  data-testid={`secret-field-view-${field.id}`}
                />
                {visibilityButton}
                {copyButton}
              </Space.Compact>
            )
          ) : isKeyBasedSelect || isMonth || isYear ? (
            <Select
              value={field.value || undefined}
              onChange={handleValueChange}
              placeholder={t('secret.fieldValue')}
              className="flex-1"
              allowClear
              options={renderSelectOptions()}
              data-testid={`secret-field-select-${field.id}`}
            />
          ) : isMultiline && !isPassword ? (
            <Input.TextArea
              value={field.value}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('secret.fieldValue')}
              className="flex-1 font-mono"
              autoSize={{ minRows: 2, maxRows: 8 }}
              data-testid={`secret-field-textarea-${field.id}`}
            />
          ) : isPassword ? (
            <div className="flex flex-1 flex-col gap-2">
              {ui?.multiline ? (
                <Input.TextArea
                  value={field.value}
                  onChange={(e) => handleValueChange(e.target.value)}
                  placeholder={t('secret.fieldValue')}
                  className="flex-1 font-mono"
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  // ponytail: CSS mask for multiline secret; Input.Password has no textarea
                  style={
                    passwordVisible
                      ? undefined
                      : ({ WebkitTextSecurity: 'disc' } as React.CSSProperties)
                  }
                />
              ) : (
                <Input
                  value={field.value}
                  onChange={(e) => handleValueChange(e.target.value)}
                  placeholder={t('secret.fieldValue')}
                  type={passwordVisible ? 'text' : 'password'}
                  className="flex-1 font-mono"
                  suffix={
                    <PasswordFeatures
                      value={field.value}
                      visible={passwordVisible}
                      readOnly={isReadOnly}
                      onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
                      onGenerate={showGenerator && !isReadOnly ? handleGeneratePassword : undefined}
                      inline
                    />
                  }
                />
              )}
              {ui?.multiline && (
                <div className="flex items-center gap-1">
                  <Tooltip
                    title={passwordVisible ? t('secret.password.hide') : t('secret.password.show')}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <Icon
                          name={passwordVisible ? 'EyeOffIcon' : 'EyeIcon'}
                          className="h-4 w-4"
                        />
                      }
                      onClick={() => setPasswordVisible(!passwordVisible)}
                    />
                  </Tooltip>
                  {showUpload && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileSelected}
                        data-testid={`secret-field-upload-input-${field.id}`}
                      />
                      <Button
                        type="dashed"
                        size="small"
                        icon={<Icon name="FileUpIcon" className="h-4 w-4" />}
                        onClick={handleUploadClick}
                        data-testid={`secret-field-upload-${field.id}`}
                      >
                        {t('secret.ssh.upload')}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {!ui?.multiline && showUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelected}
                    data-testid={`secret-field-upload-input-${field.id}`}
                  />
                  <Button
                    type="dashed"
                    size="small"
                    icon={<Icon name="FileUpIcon" className="h-4 w-4" />}
                    onClick={handleUploadClick}
                    data-testid={`secret-field-upload-${field.id}`}
                  >
                    {t('secret.ssh.upload')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Input
              value={field.value}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('secret.fieldValue')}
              type={textInputType}
              className="flex-1 font-mono"
              autoFocus={autoFocus}
              data-testid={`secret-field-input-${field.id}`}
            />
          )}
        </div>
      </div>

      {showGenerator && isPassword && !ui?.multiline && field.value && (
        <PasswordFeatures
          value={field.value}
          visible={passwordVisible}
          readOnly={isReadOnly}
          onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
          onGenerate={isReadOnly ? undefined : handleGeneratePassword}
          strengthOnly
        />
      )}

      {softWarning && (
        <span
          className="text-xs"
          style={{ color: '#ff7a45' }}
          data-testid={`secret-field-warning-${field.id}`}
        >
          {t(`secret.validation.${softWarning}`)}
        </span>
      )}
    </div>
  );
};

export default FileSecretField;
